// ═══════════════════════════════════════════════════════════════════════════
// b2mmlData.js — Layered Federation via B2MML / ISA-95 Service Bus
// ═══════════════════════════════════════════════════════════════════════════
// Source standards:
//   · ANSI/ISA-95.00.01 — Models and terminology
//   · ANSI/ISA-95.00.02 — Object model attributes
//   · ANSI/ISA-95.00.05 — B2M transactions
//   · ANSI/ISA-95.00.04 — Object models for MOM
//   · MESA B2MML v0700 — XML implementation of ISA-95 Parts 2 + 4
//   · WBF B2MML / BatchML 0700
//
// Federation principle:
//   Each ISA-95 level (L4, L3, L2, L1) is a SOVEREIGN TIER.
//   - Each tier owns its master data, its identifiers, its language.
//   - Tiers exchange via CANONICAL B2MML XML payloads on a level-segmented
//     service bus.
//   - Each tier has a GATEWAY (Personnel/Equipment/Material/Process gateway)
//     that translates between the tier's local model and the canonical
//     B2MML schema.
//   - The bus has a SCHEMA REGISTRY per ISA-95 Part (versions, compatibility).
//   - Federation is a CONTRACT, not a database — no cross-tier joins, no
//     shared keys without explicit translation.
// ═══════════════════════════════════════════════════════════════════════════

// ─── 1 · The 8 B2MML schema families (ISA-95 Part 2 + 4) ──────────────
//
// Each schema corresponds to one of the core ISA-95 information objects.
// All are XML, namespace http://www.wbf.org/xml/b2mml-v0700
//
export const B2MML_SCHEMAS = [
  {
    id: "PERSONNEL",
    name: "Personnel",
    isaPart: "ISA-95 Part 2",
    namespace: "{B2MML-V0700}Personnel",
    rootElement: "Personnel",
    purpose: "Operators, supervisors, qualifications, certifications, work schedules",
    color: "#c084fc",
    icon: "👷",
    masterDataAt: "L4 (HR system of record); cached at L3 (MES)",
    bidirectional: true,
    examplePayloadIds: ["XML-PERSONNEL-001"],
    keyClasses: ["Person", "PersonnelClass", "PersonProperty", "QualificationTest", "QualificationTestResult"],
    transactionTypes: ["Get", "Show", "Process", "Change", "Cancel"],
    federationRule: "L4 is master; L3 receives Personnel.Show on change; L3 caches and uses for assignment.",
  },
  {
    id: "EQUIPMENT",
    name: "Equipment",
    isaPart: "ISA-95 Part 2",
    namespace: "{B2MML-V0700}Equipment",
    rootElement: "Equipment",
    purpose: "Machines, cells, work centres, fixtures — hierarchy and capability",
    color: "#fb923c",
    icon: "⚙️",
    masterDataAt: "L3 (MES/Opcenter); L4 (ERP/EAM) holds financial view",
    bidirectional: true,
    examplePayloadIds: ["XML-EQUIPMENT-001"],
    keyClasses: ["Equipment", "EquipmentClass", "EquipmentProperty", "EquipmentCapabilityTest", "EquipmentLevel"],
    transactionTypes: ["Get", "Show", "Process", "Change"],
    federationRule: "L3 owns operational view; L4 owns asset/financial view; AIG keeps them in sync via Equipment.Change.",
  },
  {
    id: "MATERIAL",
    name: "Material",
    isaPart: "ISA-95 Part 2",
    namespace: "{B2MML-V0700}Material",
    rootElement: "Material",
    purpose: "Raw, WIP, finished goods — definitions, lots, sublots, locations",
    color: "#ffd600",
    icon: "📦",
    masterDataAt: "L4 (SAP material master); L3 holds lot/sublot",
    bidirectional: true,
    examplePayloadIds: ["XML-MATERIAL-001", "XML-MATERIAL-002"],
    keyClasses: ["MaterialDefinition", "MaterialClass", "MaterialLot", "MaterialSubLot", "MaterialAssembly"],
    transactionTypes: ["Get", "Show", "Process", "Change", "Cancel"],
    federationRule: "L4 master for definitions; L3 master for lots/sublots; bidirectional MaterialLot.Show on movements.",
  },
  {
    id: "PROCESS_SEGMENT",
    name: "ProcessSegment",
    isaPart: "ISA-95 Part 2",
    namespace: "{B2MML-V0700}ProcessSegment",
    rootElement: "ProcessSegment",
    purpose: "Reusable work steps with resource requirements (the 'recipe' of how to do)",
    color: "#00d4c0",
    icon: "🔗",
    masterDataAt: "L3 (MES) — derived from L4 routing + L3 BoP",
    bidirectional: false,
    examplePayloadIds: ["XML-PROCESSSEG-001"],
    keyClasses: ["ProcessSegment", "ProcessSegmentParameter", "PersonnelSegmentSpecification",
                 "EquipmentSegmentSpecification", "MaterialSegmentSpecification"],
    transactionTypes: ["Get", "Show", "Change"],
    federationRule: "L3 owns. L4 may request via ProcessSegment.Get for cost/capability planning.",
  },
  {
    id: "OPERATIONS_DEFINITION",
    name: "OperationsDefinition",
    isaPart: "ISA-95 Part 4",
    namespace: "{B2MML-V0700}OperationsDefinition",
    rootElement: "OperationsDefinition",
    purpose: "Full operations definition — segments + spec + binding to a specific run",
    color: "#38a8ff",
    icon: "📋",
    masterDataAt: "L3 (MES) — composed from segments + product definition",
    bidirectional: false,
    examplePayloadIds: ["XML-OPDEF-001"],
    keyClasses: ["OperationsDefinition", "OperationsSegment", "ParameterSpecification", "PersonnelSpecification",
                 "EquipmentSpecification", "PhysicalAssetSpecification"],
    transactionTypes: ["Get", "Show", "Change"],
    federationRule: "L3 owns; L4 receives Show for cost planning. Schema-versioned.",
  },
  {
    id: "OPERATIONS_SCHEDULE",
    name: "OperationsSchedule",
    isaPart: "ISA-95 Part 4",
    namespace: "{B2MML-V0700}OperationsSchedule",
    rootElement: "OperationsSchedule",
    purpose: "Released schedule of operations requests (work orders + sequencing)",
    color: "#ff4f8b",
    icon: "📅",
    masterDataAt: "L4 (ERP releases) → L3 (MES dispatches) → L2 (executes)",
    bidirectional: false,
    examplePayloadIds: ["XML-OPSCHED-001"],
    keyClasses: ["OperationsSchedule", "OperationsRequest", "RequestedSegmentResponse", "SegmentRequirement",
                 "PersonnelRequirement", "EquipmentRequirement", "MaterialRequirement"],
    transactionTypes: ["Get", "Show", "Process", "Change", "Cancel"],
    federationRule: "L4 → L3 via Process transaction. L3 acknowledges via Show. Critical for tier-1 SLA.",
  },
  {
    id: "OPERATIONS_PERFORMANCE",
    name: "OperationsPerformance",
    isaPart: "ISA-95 Part 4",
    namespace: "{B2MML-V0700}OperationsPerformance",
    rootElement: "OperationsPerformance",
    purpose: "Actuals — what really happened: confirmations, consumption, yield, scrap",
    color: "#00ff88",
    icon: "✅",
    masterDataAt: "L3 (MES) — sources from L2 confirmations; flows to L4",
    bidirectional: false,
    examplePayloadIds: ["XML-OPPERF-001"],
    keyClasses: ["OperationsPerformance", "OperationsResponse", "ActualSegmentResponse",
                 "PersonnelActual", "EquipmentActual", "MaterialActual"],
    transactionTypes: ["Show", "Process"],
    federationRule: "L3 → L4 via Show after operation completion. Exactly-once delivery required (finance impact).",
  },
  {
    id: "OPERATIONS_CAPABILITY",
    name: "OperationsCapability",
    isaPart: "ISA-95 Part 4",
    namespace: "{B2MML-V0700}OperationsCapability",
    rootElement: "OperationsCapability",
    purpose: "What L3 says it can do — available capacity, committed capacity, by segment",
    color: "#a78bfa",
    icon: "📊",
    masterDataAt: "L3 (MES) — computed from current state",
    bidirectional: false,
    examplePayloadIds: ["XML-OPCAP-001"],
    keyClasses: ["OperationsCapability", "PersonnelCapability", "EquipmentCapability", "MaterialCapability",
                 "ProcessSegmentCapability"],
    transactionTypes: ["Get", "Show", "Change"],
    federationRule: "L4 (APS/ERP) requests via Get; L3 responds via Show. Used for what-if planning.",
  },
];

// ─── 2 · Federation contracts (level ↔ level via specific schemas) ────
//
// Each contract describes ONE binding between two tiers, naming the
// schema(s) carried, the transaction verbs, the SLA, and the failure mode.
//
export const FEDERATION_CONTRACTS = [
  // ─ L4 ↔ L3 (the most critical band) ──────────────────────────────
  {
    id: "FED-L4L3-PERSONNEL",
    fromLevel: "L4", toLevel: "L3",
    schemas: ["PERSONNEL"],
    direction: "down",
    transactions: ["Process", "Change", "Cancel"],
    semantic: "at-least-once",
    sla: "P99 < 60s",
    rateOfChange: "low (HR-driven)",
    bus: "Enterprise bus",
    purpose: "ERP/HR pushes personnel master + qualifications to MES so that work assignments can be validated.",
    failureMode: "Operator assignments still execute against last known cache; alarm raised if cache > 24h old.",
  },
  {
    id: "FED-L4L3-EQUIPMENT",
    fromLevel: "L4", toLevel: "L3",
    schemas: ["EQUIPMENT"],
    direction: "bidirectional",
    transactions: ["Show", "Change"],
    semantic: "at-least-once",
    sla: "P99 < 60s",
    rateOfChange: "moderate (commissioning, decommissioning)",
    bus: "Enterprise bus",
    purpose: "ERP asset master ↔ MES operational equipment hierarchy. AIG / T4S handles the canonicalisation.",
    failureMode: "Equipment cannot be added to a routing until the round-trip completes. Hold + escalate.",
  },
  {
    id: "FED-L4L3-MATERIAL",
    fromLevel: "L4", toLevel: "L3",
    schemas: ["MATERIAL"],
    direction: "bidirectional",
    transactions: ["Show", "Process", "Change", "Cancel"],
    semantic: "at-least-once",
    sla: "P99 < 30s",
    rateOfChange: "very high (every movement)",
    bus: "Enterprise bus + Plant bus",
    purpose: "Material definitions: ERP master. MaterialLot: MES master. Continuous sync via MaterialLot.Show.",
    failureMode: "Lot movements buffer in outbox; ERP posting may be delayed but eventually consistent.",
  },
  {
    id: "FED-L4L3-OPSSCHED",
    fromLevel: "L4", toLevel: "L3",
    schemas: ["OPERATIONS_SCHEDULE"],
    direction: "down",
    transactions: ["Process", "Change", "Cancel"],
    semantic: "exactly-once",
    sla: "P99 < 30s",
    rateOfChange: "high (every order release)",
    bus: "Enterprise bus (tier-1)",
    purpose: "ERP releases OperationsSchedule containing OperationsRequests (work orders) to MES for dispatch.",
    failureMode: "Order release buffered in transactional outbox; deduped by RequestID at MES ingress.",
  },
  {
    id: "FED-L4L3-OPSPERF",
    fromLevel: "L3", toLevel: "L4",
    schemas: ["OPERATIONS_PERFORMANCE"],
    direction: "up",
    transactions: ["Show", "Process"],
    semantic: "exactly-once",
    sla: "P99 < 30s",
    rateOfChange: "very high (every confirmation)",
    bus: "Enterprise bus (tier-1)",
    purpose: "MES reports actuals back to ERP for cost roll-up, inventory adjustment, sales fulfilment.",
    failureMode: "Outbox + transactional retry + idempotency key on PerformanceID. Finance MUST NOT double-post.",
  },
  {
    id: "FED-L4L3-OPSCAP",
    fromLevel: "L4", toLevel: "L3",
    schemas: ["OPERATIONS_CAPABILITY"],
    direction: "bidirectional",
    transactions: ["Get", "Show"],
    semantic: "request-response",
    sla: "P99 < 10s (sync)",
    rateOfChange: "on-demand",
    bus: "Enterprise bus (sync)",
    purpose: "APS/ERP issues OperationsCapability.Get; MES responds with current available capacity per segment.",
    failureMode: "Timeout → use last known capacity (with staleness flag). Recompute on next cycle.",
  },

  // ─ L3 ↔ L2 ─────────────────────────────────────────────────────────
  {
    id: "FED-L3L2-EQUIP-STATE",
    fromLevel: "L3", toLevel: "L2",
    schemas: ["EQUIPMENT"],
    direction: "bidirectional",
    transactions: ["Show"],
    semantic: "at-least-once",
    sla: "P99 < 5s",
    rateOfChange: "very high (state changes)",
    bus: "Plant bus / OPC-UA companion",
    purpose: "L2 publishes equipment state, alarms, parameters via OPC-UA; L3 consumes for execution context.",
    failureMode: "Last-write-wins per tag. Stale-data flag if > 30s old.",
  },
  {
    id: "FED-L3L2-MATERIAL-LOT",
    fromLevel: "L3", toLevel: "L2",
    schemas: ["MATERIAL"],
    direction: "down",
    transactions: ["Process"],
    semantic: "exactly-once",
    sla: "P99 < 5s",
    rateOfChange: "per dispatch / movement",
    bus: "Plant bus",
    purpose: "L3 informs L2 which MaterialLot is currently in process at each station — for traceability.",
    failureMode: "Operation does not start until L2 ACKs lot binding.",
  },
  {
    id: "FED-L3L2-OPSDEF",
    fromLevel: "L3", toLevel: "L2",
    schemas: ["OPERATIONS_DEFINITION"],
    direction: "down",
    transactions: ["Process"],
    semantic: "at-least-once",
    sla: "P99 < 5s",
    rateOfChange: "per recipe / setup",
    bus: "Plant bus",
    purpose: "L3 pushes OperationsDefinition (recipe) to L2 control system for execution.",
    failureMode: "Recipe load aborts → operator paged.",
  },

  // ─ L2 ↔ L1 ─────────────────────────────────────────────────────────
  {
    id: "FED-L2L1-CONTROL",
    fromLevel: "L2", toLevel: "L1",
    schemas: [],
    direction: "bidirectional",
    transactions: [],
    semantic: "fieldbus-deterministic",
    sla: "P99 < 100ms",
    rateOfChange: "continuous (control loop)",
    bus: "Fieldbus (PROFINET / EtherCAT / EtherNet/IP)",
    purpose: "Below the bus — deterministic fieldbus, NOT B2MML. Listed for completeness.",
    failureMode: "Drive safety state (STO/SS1) on lost comms.",
    nonB2MML: true,
  },
];

// ─── 3 · The bus topology: per-level segment + gateway + registry ─────
//
// The federation runs on a SEGMENTED service bus, not a single bus.
// Each level has its own broker (or topic prefix on a shared broker)
// with a level-specific schema registry and authorisation policy.
//
export const BUS_TOPOLOGY = {
  segments: [
    {
      id: "ENTERPRISE_BUS",
      label: "Enterprise Bus",
      level: "L4-L3",
      tech: "Kafka MSK / Confluent Cloud",
      topics: [
        { name: "personnel.v1",            schema: "PERSONNEL",            retention: "7d",  partitions: 3 },
        { name: "equipment.v1",            schema: "EQUIPMENT",            retention: "30d", partitions: 6 },
        { name: "material.definition.v1",  schema: "MATERIAL",             retention: "30d", partitions: 12 },
        { name: "material.lot.v1",         schema: "MATERIAL",             retention: "90d", partitions: 24 },
        { name: "ops.schedule.v1",         schema: "OPERATIONS_SCHEDULE",  retention: "30d", partitions: 12 },
        { name: "ops.performance.v1",      schema: "OPERATIONS_PERFORMANCE", retention: "90d", partitions: 24 },
        { name: "ops.capability.v1",       schema: "OPERATIONS_CAPABILITY", retention: "7d",  partitions: 3, type: "request-response" },
      ],
      registry: "Confluent Schema Registry · XSD validation",
      auth: "mTLS + OAuth · per-topic ACLs",
      slaTier: "Tier-1 (99.95%)",
    },
    {
      id: "PLANT_BUS",
      label: "Plant Bus (per plant)",
      level: "L3-L2",
      tech: "Kafka on-prem / RabbitMQ + OPC-UA",
      topics: [
        { name: "ops.definition.v1",       schema: "OPERATIONS_DEFINITION", retention: "30d", partitions: 6 },
        { name: "ops.dispatch.v1",         schema: "OPERATIONS_SCHEDULE",   retention: "30d", partitions: 6 },
        { name: "equipment.state.v1",      schema: "EQUIPMENT",             retention: "7d",  partitions: 24 },
        { name: "equipment.alarm.v1",      schema: "EQUIPMENT",             retention: "30d", partitions: 6 },
        { name: "material.lot.dispatch.v1",schema: "MATERIAL",              retention: "30d", partitions: 12 },
        { name: "ops.confirmation.v1",     schema: "OPERATIONS_PERFORMANCE",retention: "30d", partitions: 12 },
      ],
      registry: "Local schema registry · replicated from enterprise",
      auth: "mTLS within OT zone · OPC-UA UserToken for legacy",
      slaTier: "Tier-1 (99.9%)",
    },
    {
      id: "FIELDBUS",
      label: "Fieldbus (per cell)",
      level: "L2-L1",
      tech: "PROFINET / EtherCAT / EtherNet/IP",
      topics: [],
      registry: "n/a (deterministic, hardware-mapped)",
      auth: "Network segmentation (IEC 62443)",
      slaTier: "Hard real-time (< 100ms)",
      nonB2MML: true,
    },
  ],
  gateways: [
    {
      id: "GW-ENT-PERSONNEL",
      level: "L4",
      segment: "ENTERPRISE_BUS",
      label: "Personnel Gateway",
      role: "Translates ERP HR/IAM payloads → B2MML Personnel; emits to personnel.v1",
      handles: ["PERSONNEL"],
      tech: "Mendix · custom adapter",
    },
    {
      id: "GW-ENT-EQUIPMENT",
      level: "L4",
      segment: "ENTERPRISE_BUS",
      label: "Asset Gateway",
      role: "Translates SAP PM / EAM → B2MML Equipment financial view",
      handles: ["EQUIPMENT"],
      tech: "T4S + custom",
    },
    {
      id: "GW-ENT-MATERIAL",
      level: "L4",
      segment: "ENTERPRISE_BUS",
      label: "Material Master Gateway",
      role: "SAP material master ↔ B2MML Material; lot updates bidirectional",
      handles: ["MATERIAL"],
      tech: "T4S BAPI/IDoc + Camel",
    },
    {
      id: "GW-ENT-ORDERS",
      level: "L4",
      segment: "ENTERPRISE_BUS",
      label: "Orders Gateway",
      role: "SAP AUFNR / VBAK / VBAP → B2MML OperationsSchedule / OperationsRequest",
      handles: ["OPERATIONS_SCHEDULE", "OPERATIONS_PERFORMANCE", "OPERATIONS_CAPABILITY"],
      tech: "CN4S + T4S",
    },
    {
      id: "GW-MES-INGRESS",
      level: "L3",
      segment: "ENTERPRISE_BUS",
      label: "MES Ingress Gateway",
      role: "Consumes B2MML; translates to Opcenter EX native objects; idempotency-keyed",
      handles: ["PERSONNEL", "EQUIPMENT", "MATERIAL", "OPERATIONS_SCHEDULE", "OPERATIONS_CAPABILITY"],
      tech: "Opcenter SOA + custom Camel routes",
    },
    {
      id: "GW-MES-EGRESS",
      level: "L3",
      segment: "ENTERPRISE_BUS",
      label: "MES Egress Gateway",
      role: "Composes B2MML OperationsPerformance / MaterialLot from MES events",
      handles: ["OPERATIONS_PERFORMANCE", "MATERIAL", "EQUIPMENT"],
      tech: "Opcenter outbox + Camel + Avro serializer",
    },
    {
      id: "GW-PLANT-DISPATCH",
      level: "L3",
      segment: "PLANT_BUS",
      label: "Dispatch Gateway",
      role: "MES → L2: publishes OperationsDefinition + MaterialLot binding per station",
      handles: ["OPERATIONS_DEFINITION", "MATERIAL"],
      tech: "Opcenter dispatcher + plant Kafka",
    },
    {
      id: "GW-PLANT-EDGE",
      level: "L2",
      segment: "PLANT_BUS",
      label: "Industrial Edge Gateway",
      role: "OPC-UA companion ↔ Equipment.Show; alarm normalisation",
      handles: ["EQUIPMENT"],
      tech: "Industrial Edge + IIH",
    },
    {
      id: "GW-PLANT-CONF",
      level: "L2",
      segment: "PLANT_BUS",
      label: "Confirmation Gateway",
      role: "L2 confirmations → B2MML OperationsPerformance (delta)",
      handles: ["OPERATIONS_PERFORMANCE"],
      tech: "SFC terminals + Opcenter ingest",
    },
  ],
};

// ─── 4 · XML payload samples ──────────────────────────────────────────
// Real B2MML v0700 fragments. Annotated. Used by the payload viewer.
//
export const XML_PAYLOADS = {
  "XML-PERSONNEL-001": {
    title: "Personnel.Show — one operator with qualifications",
    schema: "PERSONNEL",
    transaction: "Show",
    direction: "L4 → L3",
    xml: `<?xml version="1.0" encoding="UTF-8"?>
<Personnel xmlns="http://www.wbf.org/xml/b2mml-v0700">
  <ID>OP-44192</ID>
  <Description>Maria Schmidt — Senior CNC Operator</Description>
  <HierarchyScope>
    <EquipmentID>PLANT-MUNICH-CELL-7</EquipmentID>
    <EquipmentElementLevel>WorkCell</EquipmentElementLevel>
  </HierarchyScope>
  <PersonnelClassID>CNC-Operator-L3</PersonnelClassID>
  <PersonProperty>
    <ID>ShiftAssignment</ID>
    <Value>
      <ValueString>Late-Shift-B</ValueString>
    </Value>
  </PersonProperty>
  <PersonProperty>
    <ID>Qualification</ID>
    <Value>
      <ValueString>5-Axis-CNC-Certified</ValueString>
    </Value>
    <Value>
      <ValueString>Sinumerik-840D-L4</ValueString>
    </Value>
  </PersonProperty>
  <QualificationTestResult>
    <ID>QT-2024-CERT-1142</ID>
    <Description>Annual recertification</Description>
    <ResultDateTime>2025-08-12T09:30:00Z</ResultDateTime>
    <ExpirationDateTime>2026-08-12T09:30:00Z</ExpirationDateTime>
  </QualificationTestResult>
</Personnel>`,
    notes: [
      "ID is the canonical personnel identifier across all tiers.",
      "HierarchyScope binds this person to a specific equipment hierarchy node — ISA-95 equipment model.",
      "PersonnelClassID groups operators by class for class-level work assignment rules.",
      "QualificationTestResult enables MES to refuse assignment if expired.",
    ],
  },

  "XML-EQUIPMENT-001": {
    title: "Equipment.Show — CNC machine with capabilities",
    schema: "EQUIPMENT",
    transaction: "Show",
    direction: "L3 ↔ L4 (bidirectional)",
    xml: `<?xml version="1.0" encoding="UTF-8"?>
<Equipment xmlns="http://www.wbf.org/xml/b2mml-v0700">
  <ID>CNC-MUNICH-CELL-7-MILL-03</ID>
  <Description>DMG MORI NMV5000 5-axis vertical mill</Description>
  <HierarchyScope>
    <EquipmentID>PLANT-MUNICH-CELL-7</EquipmentID>
    <EquipmentElementLevel>WorkCell</EquipmentElementLevel>
  </HierarchyScope>
  <EquipmentLevel>Unit</EquipmentLevel>
  <EquipmentClassID>5-Axis-Mill</EquipmentClassID>
  <Location>
    <EquipmentElementLevel>WorkCell</EquipmentElementLevel>
    <EquipmentID>PLANT-MUNICH-CELL-7</EquipmentID>
  </Location>
  <EquipmentProperty>
    <ID>SpindleSpeedMax_rpm</ID>
    <Value>
      <ValueString>15000</ValueString>
      <DataType>decimal</DataType>
      <UnitOfMeasure>rpm</UnitOfMeasure>
    </Value>
  </EquipmentProperty>
  <EquipmentProperty>
    <ID>WorkEnvelopeX_mm</ID>
    <Value>
      <ValueString>800</ValueString>
      <DataType>decimal</DataType>
      <UnitOfMeasure>mm</UnitOfMeasure>
    </Value>
  </EquipmentProperty>
  <EquipmentCapabilityTestSpecification>
    <ID>5AxisCertified</ID>
    <Description>Annual 5-axis precision test</Description>
  </EquipmentCapabilityTestSpecification>
</Equipment>`,
    notes: [
      "EquipmentLevel = ISA-95 model (Enterprise → Site → Area → WorkCenter → WorkUnit → Equipment).",
      "EquipmentClassID groups for capability-based routing (any machine in class 5-Axis-Mill can run this op).",
      "Each EquipmentProperty carries a DataType + UnitOfMeasure — engineering units are explicit.",
      "Federation rule: L4 owns asset financial view (purchase, depreciation); L3 owns operational view.",
    ],
  },

  "XML-MATERIAL-001": {
    title: "Material.Show — definition of a finished product",
    schema: "MATERIAL",
    transaction: "Show",
    direction: "L4 → L3",
    xml: `<?xml version="1.0" encoding="UTF-8"?>
<Material xmlns="http://www.wbf.org/xml/b2mml-v0700">
  <MaterialDefinition>
    <ID>MAT-FG-AXLE-7301</ID>
    <Description>Drive axle assembly, model 7301-A</Description>
    <MaterialDefinitionID>MAT-FG-AXLE-7301</MaterialDefinitionID>
    <MaterialClassID>Finished-Goods-Powertrain</MaterialClassID>
    <Status>
      <StatusType>Released</StatusType>
    </Status>
    <UnitOfMeasure>EA</UnitOfMeasure>
    <MaterialDefinitionProperty>
      <ID>Weight_kg</ID>
      <Value>
        <ValueString>14.82</ValueString>
        <DataType>decimal</DataType>
        <UnitOfMeasure>kg</UnitOfMeasure>
      </Value>
    </MaterialDefinitionProperty>
    <MaterialDefinitionProperty>
      <ID>Drawing</ID>
      <Value>
        <ValueString>DWG-AXLE-7301-RevD</ValueString>
      </Value>
    </MaterialDefinitionProperty>
  </MaterialDefinition>
</Material>`,
    notes: [
      "MaterialDefinition is the SAP material master canonicalised to B2MML.",
      "MaterialClassID groups for class-level rules and aggregations.",
      "Status drives lifecycle: Draft → Released → Obsolete.",
      "Properties carry full unit-of-measure → no ambiguity at the tier boundary.",
    ],
  },

  "XML-MATERIAL-002": {
    title: "MaterialLot.Show — a specific lot at a specific stage",
    schema: "MATERIAL",
    transaction: "Show",
    direction: "L3 → L4",
    xml: `<?xml version="1.0" encoding="UTF-8"?>
<Material xmlns="http://www.wbf.org/xml/b2mml-v0700">
  <MaterialLot>
    <ID>LOT-7301-2025-W47-0042</ID>
    <MaterialDefinitionID>MAT-FG-AXLE-7301</MaterialDefinitionID>
    <Status>
      <StatusType>InProgress</StatusType>
    </Status>
    <Quantity>
      <QuantityString>24</QuantityString>
      <DataType>integer</DataType>
      <UnitOfMeasure>EA</UnitOfMeasure>
    </Quantity>
    <Location>
      <EquipmentID>PLANT-MUNICH-CELL-7</EquipmentID>
      <EquipmentElementLevel>WorkCell</EquipmentElementLevel>
    </Location>
    <AssemblyType>Lot</AssemblyType>
    <MaterialSubLot>
      <ID>SL-7301-W47-0042-A</ID>
      <Quantity>
        <QuantityString>12</QuantityString>
        <UnitOfMeasure>EA</UnitOfMeasure>
      </Quantity>
      <Location>
        <EquipmentID>PLANT-MUNICH-CELL-7-MILL-03</EquipmentID>
      </Location>
    </MaterialSubLot>
  </MaterialLot>
</Material>`,
    notes: [
      "MaterialLot is OWNED BY L3 — finer granularity than ERP's MARC/MARD typically carries.",
      "Each subload tracks split lots at sub-equipment level.",
      "ERP receives MaterialLot.Show on every state transition; ERP updates MARC/MSEG via its own logic.",
      "Critical for genealogy and recall — IATF/ISO 22005 requirement.",
    ],
  },

  "XML-PROCESSSEG-001": {
    title: "ProcessSegment.Show — reusable rough-mill segment",
    schema: "PROCESS_SEGMENT",
    transaction: "Show",
    direction: "L3 internal",
    xml: `<?xml version="1.0" encoding="UTF-8"?>
<ProcessSegment xmlns="http://www.wbf.org/xml/b2mml-v0700">
  <ID>PS-ROUGH-MILL-5AX-V1</ID>
  <Description>Rough milling, 5-axis, axle-7301 family</Description>
  <Duration>
    <DurationString>PT12M30S</DurationString>
  </Duration>
  <PersonnelSegmentSpecification>
    <PersonnelClassID>CNC-Operator-L3</PersonnelClassID>
    <Quantity>
      <QuantityString>1</QuantityString>
      <UnitOfMeasure>EA</UnitOfMeasure>
    </Quantity>
  </PersonnelSegmentSpecification>
  <EquipmentSegmentSpecification>
    <EquipmentClassID>5-Axis-Mill</EquipmentClassID>
    <Quantity>
      <QuantityString>1</QuantityString>
      <UnitOfMeasure>EA</UnitOfMeasure>
    </Quantity>
  </EquipmentSegmentSpecification>
  <MaterialSegmentSpecification>
    <MaterialClassID>Forging-Blanks-Axle</MaterialClassID>
    <MaterialUse>Consumed</MaterialUse>
    <Quantity>
      <QuantityString>1</QuantityString>
      <UnitOfMeasure>EA</UnitOfMeasure>
    </Quantity>
  </MaterialSegmentSpecification>
  <MaterialSegmentSpecification>
    <MaterialClassID>Cutting-Tool-D12-Carbide</MaterialClassID>
    <MaterialUse>Tool</MaterialUse>
    <Quantity>
      <QuantityString>1</QuantityString>
      <UnitOfMeasure>EA</UnitOfMeasure>
    </Quantity>
  </MaterialSegmentSpecification>
</ProcessSegment>`,
    notes: [
      "ProcessSegment is the reusable atomic work step in ISA-95.",
      "Specifications are CLASS-based — any operator/equipment/material in the class satisfies the segment.",
      "Bound to actual instances at OperationsRequest time, allowing late-binding for flexibility.",
      "Duration is ISO 8601 — PT12M30S = 12 minutes 30 seconds.",
    ],
  },

  "XML-OPDEF-001": {
    title: "OperationsDefinition.Show — full def for axle production",
    schema: "OPERATIONS_DEFINITION",
    transaction: "Show",
    direction: "L3 internal",
    xml: `<?xml version="1.0" encoding="UTF-8"?>
<OperationsDefinition xmlns="http://www.wbf.org/xml/b2mml-v0700">
  <ID>OPDEF-AXLE-7301-V3</ID>
  <Description>Axle 7301-A complete operations definition</Description>
  <OperationsType>Production</OperationsType>
  <PublishedDate>2025-09-01T00:00:00Z</PublishedDate>
  <OperationsSegment>
    <ID>SEG-1-ROUGH-MILL</ID>
    <ProcessSegmentID>PS-ROUGH-MILL-5AX-V1</ProcessSegmentID>
    <ExecutionDependency>None</ExecutionDependency>
  </OperationsSegment>
  <OperationsSegment>
    <ID>SEG-2-FINISH-MILL</ID>
    <ProcessSegmentID>PS-FINISH-MILL-5AX-V1</ProcessSegmentID>
    <ExecutionDependency>SEG-1-ROUGH-MILL</ExecutionDependency>
  </OperationsSegment>
  <OperationsSegment>
    <ID>SEG-3-INSPECT-CMM</ID>
    <ProcessSegmentID>PS-CMM-INSPECT-AXLE</ProcessSegmentID>
    <ExecutionDependency>SEG-2-FINISH-MILL</ExecutionDependency>
  </OperationsSegment>
  <ParameterSpecification>
    <ID>SpindleSpeed_rpm</ID>
    <Value>
      <ValueString>12000</ValueString>
      <UnitOfMeasure>rpm</UnitOfMeasure>
    </Value>
  </ParameterSpecification>
</OperationsDefinition>`,
    notes: [
      "Composes ProcessSegments into a directed graph via ExecutionDependency.",
      "OperationsType (Production / Inventory / Quality / Maintenance) governs which L4 module receives reports.",
      "Parameters at OperationsDefinition level override segment-level — recipe overrides.",
      "Versioning is mandatory; older versions kept for traceability.",
    ],
  },

  "XML-OPSCHED-001": {
    title: "OperationsSchedule.Process — release a work order to MES",
    schema: "OPERATIONS_SCHEDULE",
    transaction: "Process",
    direction: "L4 → L3",
    xml: `<?xml version="1.0" encoding="UTF-8"?>
<OperationsSchedule xmlns="http://www.wbf.org/xml/b2mml-v0700">
  <ID>SCHED-2025-W47-MUNICH</ID>
  <Description>Munich plant schedule, week 47/2025</Description>
  <PublishedDate>2025-11-17T06:00:00Z</PublishedDate>
  <StartTime>2025-11-17T06:00:00Z</StartTime>
  <EndTime>2025-11-23T22:00:00Z</EndTime>
  <OperationsType>Production</OperationsType>
  <OperationsRequest>
    <ID>WO-AUFNR-1099842</ID>
    <Description>Axle 7301-A — qty 24 — customer KLM-Aero</Description>
    <Priority>Normal</Priority>
    <StartTime>2025-11-19T07:00:00Z</StartTime>
    <EndTime>2025-11-19T15:30:00Z</EndTime>
    <OperationsDefinitionID>OPDEF-AXLE-7301-V3</OperationsDefinitionID>
    <SegmentRequirement>
      <ID>REQ-SEG-1</ID>
      <ProcessSegmentID>PS-ROUGH-MILL-5AX-V1</ProcessSegmentID>
      <EarliestStartTime>2025-11-19T07:00:00Z</EarliestStartTime>
      <MaterialRequirement>
        <MaterialDefinitionID>MAT-FG-AXLE-7301</MaterialDefinitionID>
        <MaterialUse>Produced</MaterialUse>
        <Quantity>
          <QuantityString>24</QuantityString>
          <UnitOfMeasure>EA</UnitOfMeasure>
        </Quantity>
      </MaterialRequirement>
    </SegmentRequirement>
  </OperationsRequest>
</OperationsSchedule>`,
    notes: [
      "OperationsSchedule is the ENTIRE released horizon; contains many OperationsRequests.",
      "ID at the request level (WO-AUFNR-1099842) maps to SAP AUFNR — the federation key.",
      "Process transaction = 'please execute this'. MES responds with Show (acknowledge).",
      "EXACTLY-ONCE delivery semantic — dedupe key is OperationsRequest.ID.",
    ],
  },

  "XML-OPPERF-001": {
    title: "OperationsPerformance.Show — confirmation back to ERP",
    schema: "OPERATIONS_PERFORMANCE",
    transaction: "Show",
    direction: "L3 → L4",
    xml: `<?xml version="1.0" encoding="UTF-8"?>
<OperationsPerformance xmlns="http://www.wbf.org/xml/b2mml-v0700">
  <ID>PERF-WO-1099842-2025-11-19</ID>
  <Description>Confirmation for WO-AUFNR-1099842</Description>
  <PublishedDate>2025-11-19T15:42:08Z</PublishedDate>
  <OperationsType>Production</OperationsType>
  <OperationsResponse>
    <ID>RESP-1099842</ID>
    <Description>Confirmation against WO-AUFNR-1099842</Description>
    <OperationsRequestID>WO-AUFNR-1099842</OperationsRequestID>
    <Hierarchy>
      <EquipmentID>PLANT-MUNICH-CELL-7</EquipmentID>
    </Hierarchy>
    <ActualStartTime>2025-11-19T07:04:12Z</ActualStartTime>
    <ActualEndTime>2025-11-19T15:38:55Z</ActualEndTime>
    <ActualSegmentResponse>
      <ID>RESP-SEG-1</ID>
      <ProcessSegmentID>PS-ROUGH-MILL-5AX-V1</ProcessSegmentID>
      <ActualStartTime>2025-11-19T07:04:12Z</ActualStartTime>
      <ActualEndTime>2025-11-19T09:56:30Z</ActualEndTime>
      <PersonnelActual>
        <PersonnelID>OP-44192</PersonnelID>
        <Quantity>
          <QuantityString>2.875</QuantityString>
          <UnitOfMeasure>h</UnitOfMeasure>
        </Quantity>
      </PersonnelActual>
      <EquipmentActual>
        <EquipmentID>CNC-MUNICH-CELL-7-MILL-03</EquipmentID>
        <Quantity>
          <QuantityString>2.875</QuantityString>
          <UnitOfMeasure>h</UnitOfMeasure>
        </Quantity>
      </EquipmentActual>
      <MaterialActual>
        <MaterialDefinitionID>MAT-FG-AXLE-7301</MaterialDefinitionID>
        <MaterialLotID>LOT-7301-2025-W47-0042</MaterialLotID>
        <MaterialUse>Produced</MaterialUse>
        <Quantity>
          <QuantityString>24</QuantityString>
          <UnitOfMeasure>EA</UnitOfMeasure>
        </Quantity>
      </MaterialActual>
      <MaterialActual>
        <MaterialDefinitionID>SCRAP-AXLE-7301</MaterialDefinitionID>
        <MaterialUse>Produced</MaterialUse>
        <Quantity>
          <QuantityString>1</QuantityString>
          <UnitOfMeasure>EA</UnitOfMeasure>
        </Quantity>
      </MaterialActual>
    </ActualSegmentResponse>
  </OperationsResponse>
</OperationsPerformance>`,
    notes: [
      "OperationsRequestID is the federation key back to SAP AUFNR — exactly-once semantic.",
      "Personnel/Equipment/Material actuals are the raw input for SAP cost roll-up.",
      "Scrap reported as a separate MaterialActual with MaterialUse=Produced — for COPQ tracking.",
      "P99 SLA < 30s — finance close depends on this timeliness.",
    ],
  },

  "XML-OPCAP-001": {
    title: "OperationsCapability.Show — what L3 says it can do",
    schema: "OPERATIONS_CAPABILITY",
    transaction: "Show",
    direction: "L3 → L4 (in response to Get)",
    xml: `<?xml version="1.0" encoding="UTF-8"?>
<OperationsCapability xmlns="http://www.wbf.org/xml/b2mml-v0700">
  <ID>CAP-MUNICH-2025-W48</ID>
  <PublishedDate>2025-11-17T07:00:00Z</PublishedDate>
  <StartTime>2025-11-24T00:00:00Z</StartTime>
  <EndTime>2025-11-30T23:59:59Z</EndTime>
  <Reason>Capable</Reason>
  <PersonnelCapability>
    <PersonnelClassID>CNC-Operator-L3</PersonnelClassID>
    <CapabilityType>Available</CapabilityType>
    <Quantity>
      <QuantityString>168</QuantityString>
      <UnitOfMeasure>h</UnitOfMeasure>
    </Quantity>
  </PersonnelCapability>
  <PersonnelCapability>
    <PersonnelClassID>CNC-Operator-L3</PersonnelClassID>
    <CapabilityType>Committed</CapabilityType>
    <Quantity>
      <QuantityString>132</QuantityString>
      <UnitOfMeasure>h</UnitOfMeasure>
    </Quantity>
  </PersonnelCapability>
  <EquipmentCapability>
    <EquipmentClassID>5-Axis-Mill</EquipmentClassID>
    <CapabilityType>Available</CapabilityType>
    <Quantity>
      <QuantityString>504</QuantityString>
      <UnitOfMeasure>h</UnitOfMeasure>
    </Quantity>
  </EquipmentCapability>
  <ProcessSegmentCapability>
    <ProcessSegmentID>PS-ROUGH-MILL-5AX-V1</ProcessSegmentID>
    <CapabilityType>Unattainable</CapabilityType>
    <Reason>Tool inventory short for D12 carbide</Reason>
    <Quantity>
      <QuantityString>30</QuantityString>
      <UnitOfMeasure>h</UnitOfMeasure>
    </Quantity>
  </ProcessSegmentCapability>
</OperationsCapability>`,
    notes: [
      "CapabilityType enum: Available | Committed | Unattainable.",
      "Class-level capability allows APS/ERP to do capacity planning without knowing individual machines.",
      "Unattainable + Reason is the key insight — APS sees this and re-plans.",
      "Request/response pattern — synchronous semantic, 10s timeout.",
    ],
  },
};

// ─── 5 · ISA-95 Levels (reused/extended) ──────────────────────────────
export const ISA95_LEVELS = [
  { id: "L4", name: "Enterprise",         color: "#ffd600", systems: ["ERP", "PLM", "Cloud", "HR", "APS"] },
  { id: "L3", name: "MOM / MES",          color: "#38a8ff", systems: ["Opcenter EX", "QM", "EasyPlan"] },
  { id: "L2", name: "Process Control",    color: "#fb923c", systems: ["SCADA", "Industrial Edge", "OPC-UA"] },
  { id: "L1", name: "Sensors / Devices",  color: "#00d4c0", systems: ["PLC", "CNC", "Robot", "IIoT"], nonB2MML: true },
  { id: "L0", name: "Physical Process",   color: "#64b5d6", systems: ["Conveyors", "Production"],   nonB2MML: true },
];

// ─── 6 · Convenience selectors ────────────────────────────────────────
export const schemaById        = (id) => B2MML_SCHEMAS.find(s => s.id === id);
export const contractsBySchema = (id) => FEDERATION_CONTRACTS.filter(c => c.schemas?.includes(id));
export const contractsByLevel  = (l)  => FEDERATION_CONTRACTS.filter(c => c.fromLevel === l || c.toLevel === l);
export const payloadById       = (id) => XML_PAYLOADS[id];
export const payloadsBySchema  = (id) => Object.entries(XML_PAYLOADS)
                                          .filter(([, p]) => p.schema === id)
                                          .map(([k, v]) => ({ id: k, ...v }));

// ─── 6b · B2MML schema → CLM interface mapping ────────────────────────
//
// For each schema, which CLM interfaces carry payloads conforming to it?
// Hand-curated — used by the federation tab to cross-link into the LLD tab.
//
export const SCHEMA_TO_INTERFACES = {
  PERSONNEL:              ["IF-005"],                                              // operator master sync
  EQUIPMENT:              ["IF-005", "IF-022"],                                    // asset master + equipment state
  MATERIAL:               ["IF-013", "IF-027"],                                    // material master + lot
  PROCESS_SEGMENT:        ["IF-001", "IF-002", "IF-003", "IF-004"],                // routing / BOP family
  OPERATIONS_DEFINITION:  ["IF-002", "IF-003", "IF-006"],                          // BOM + recipe
  OPERATIONS_SCHEDULE:    ["IF-014", "IF-015", "IF-016", "IF-017"],                // order release + dispatch
  OPERATIONS_PERFORMANCE: ["IF-017", "IF-027"],                                    // confirmation + AFRU
  OPERATIONS_CAPABILITY:  ["IF-016"],                                              // capacity request/response
};

export const interfacesForSchema = (schemaId) => SCHEMA_TO_INTERFACES[schemaId] || [];

// ─── 7 · Aggregate stats ──────────────────────────────────────────────
export const B2MML_STATS = {
  totalSchemas:    B2MML_SCHEMAS.length,
  totalContracts:  FEDERATION_CONTRACTS.length,
  totalPayloads:   Object.keys(XML_PAYLOADS).length,
  busSegments:     BUS_TOPOLOGY.segments.length,
  gateways:        BUS_TOPOLOGY.gateways.length,
  tier1Contracts:  FEDERATION_CONTRACTS.filter(c => c.semantic === "exactly-once").length,
  bidirectional:   FEDERATION_CONTRACTS.filter(c => c.direction === "bidirectional").length,
  totalInterfaceLinks: Object.values(SCHEMA_TO_INTERFACES).reduce((a, v) => a + v.length, 0),
};
